import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Trash2, ShoppingBag, Package } from 'lucide-react';
import { cartLineKey, cartItemQueryParams } from '@/lib/supplierCartApi';

import { getApiBaseUrl } from '@/lib/apiBase';

const API = getApiBaseUrl();

const formatMoney = (n) =>
  n == null ? '0' : Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

const CartPage = () => {
  const { token } = useAuth();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creatingOrder, setCreatingOrder] = useState(null);
  const [creatingAll, setCreatingAll] = useState(false);
  const [localQty, setLocalQty] = useState({});
  const pendingTimers = useRef({});
  const lastGoodQty = useRef({});

  const fetchCart = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/cart`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCart(data);
    } catch {
      toast.error('Ошибка загрузки корзины');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchCart(); }, [fetchCart]);

  // Sync localQty from server data, but only for items without a pending timer
  useEffect(() => {
    if (!cart?.items) return;
    setLocalQty((prev) => {
      const next = { ...prev };
      for (const item of cart.items) {
        const lineKey = cartLineKey(item);
        if (!pendingTimers.current[lineKey]) {
          next[lineKey] = item.quantity;
          lastGoodQty.current[lineKey] = item.quantity;
        }
      }
      return next;
    });
  }, [cart]);

  const handleQtyChange = useCallback((item, rawVal, packingRate = 1) => {
    const lineKey = cartLineKey(item);
    const parsed = parseInt(rawVal);
    const qty = isNaN(parsed) || parsed < 1 ? packingRate : Math.max(packingRate, parsed);

    setLocalQty((prev) => ({ ...prev, [lineKey]: qty }));

    if (pendingTimers.current[lineKey]) {
      clearTimeout(pendingTimers.current[lineKey]);
    }

    pendingTimers.current[lineKey] = setTimeout(async () => {
      delete pendingTimers.current[lineKey];
      try {
        const { data } = await axios.put(
          `${API}/cart/items/${item.product_id}`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { quantity: qty, ...cartItemQueryParams(item) },
          },
        );
        setCart(data.cart);
        lastGoodQty.current[lineKey] = qty;
      } catch {
        toast.error('Ошибка обновления количества');
        setLocalQty((prev) => ({
          ...prev,
          [lineKey]: lastGoodQty.current[lineKey] ?? prev[lineKey],
        }));
      }
    }, 400);
  }, [token]);

  const removeItem = useCallback(async (item) => {
    const lineKey = cartLineKey(item);
    setCart((prev) => {
      if (!prev) return prev;
      const items = prev.items.filter((i) => cartLineKey(i) !== lineKey);
      return { ...prev, items, total_amount: items.reduce((s, i) => s + i.subtotal, 0) };
    });
    try {
      await axios.delete(`${API}/cart/items/${item.product_id}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: cartItemQueryParams(item),
      });
      fetchCart();
    } catch {
      toast.error('Ошибка удаления товара');
      fetchCart();
    }
  }, [token, fetchCart]);

  const createOrder = useCallback(async (supplierId) => {
    if (!supplierId) return;
    setCreatingOrder(supplierId);
    try {
      const { data } = await axios.post(
        `${API}/orders`,
        { supplier_id: supplierId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (data.supplier_name === 'Интерком') {
        toast.success('Заказ оформлен, отправка в Интерком выполняется в фоне');
      } else {
        toast.success('Заказ оформлен');
      }
      fetchCart();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Ошибка оформления заказа');
    } finally {
      setCreatingOrder(null);
    }
  }, [token, fetchCart]);

  const createAllOrders = useCallback(async () => {
    setCreatingAll(true);
    try {
      const { data } = await axios.post(
        `${API}/orders/all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Оформлено заказов: ${data.count}`);
      fetchCart();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Ошибка оформления заказов');
    } finally {
      setCreatingAll(false);
    }
  }, [token, fetchCart]);

  const supplierGroups = useMemo(() => {
    const map = {};
    for (const item of (cart?.items || [])) {
      const sid = item.supplier_id || 'unknown';
      if (!map[sid]) {
        map[sid] = {
          supplier_id: sid,
          supplier_name: item.supplier_name || 'Поставщик',
          items: [],
          total: 0,
        };
      }
      map[sid].items.push(item);
      map[sid].total += Number(item.subtotal || 0);
    }
    return Object.values(map);
  }, [cart]);

  const grandTotal = useMemo(() => {
    if (!cart?.items) return 0;
    return cart.items.reduce((sum, item) => {
      const qty = localQty[cartLineKey(item)] ?? item.quantity;
      return sum + qty * item.price;
    }, 0);
  }, [cart, localQty]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!cart || cart.items.length === 0) {
    return (
      <div className="text-center py-16" data-testid="empty-cart">
        <ShoppingBag className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Корзина пуста</h2>
        <p className="text-gray-600">Добавьте товары из каталога</p>
      </div>
    );
  }

  const anyBusy = !!creatingOrder || creatingAll;

  return (
    <div className="space-y-6" data-testid="cart-page">
      <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>
        Корзина
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ── Items grouped by supplier ─────────────────────────────────── */}
        <div className="lg:col-span-2 space-y-6">
          {supplierGroups.map((group) => (
            <div key={group.supplier_id}>
              <div className="flex items-center gap-2 mb-2 px-1">
                <Package className="w-4 h-4 text-indigo-400" />
                <span className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                  {group.supplier_name}
                </span>
                <span className="text-xs text-gray-400 ml-auto">
                  {group.items.length} поз.
                </span>
              </div>

              <div className="space-y-3">
                {group.items.map((item, index) => {
                  const lineKey = cartLineKey(item);
                  const qty = localQty[lineKey] ?? item.quantity;
                  const step = item.packing_rate || 1;
                  const subtotal = qty * item.price;

                  return (
                    <Card
                      key={lineKey}
                      className="bg-white/90 backdrop-blur-sm"
                      data-testid={`cart-item-${index}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900 truncate">
                              {item.product_name}
                            </h3>
                            <p className="text-sm text-gray-500">Код: {item.product_code}</p>
                            {item.for_client && (
                              <p className="text-xs font-medium text-indigo-600 mt-0.5">
                                Под клиента
                                {item.customer_order_number ? ` · ${item.customer_order_number}` : ''}
                              </p>
                            )}
                            <div className="flex items-center gap-2 mt-1">
                              <p className="text-base font-bold text-blue-600">
                                {formatMoney(item.price)} ₸
                              </p>
                              {step > 1 && (
                                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                                  кратно {step}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-3 flex-shrink-0">
                            <div className="flex items-center gap-1.5">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => handleQtyChange(item, qty - step, step)}
                                data-testid={`decrease-quantity-${index}`}
                              >
                                −
                              </Button>
                              <Input
                                type="number"
                                value={qty}
                                onChange={(e) =>
                                  handleQtyChange(item, e.target.value, step)
                                }
                                className="w-16 h-8 text-center text-sm"
                                data-testid={`quantity-input-${index}`}
                              />
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => handleQtyChange(item, qty + step, step)}
                                data-testid={`increase-quantity-${index}`}
                              >
                                +
                              </Button>
                            </div>

                            <div className="text-right min-w-[80px]">
                              <p className="font-bold text-gray-900 text-sm">
                                {formatMoney(subtotal)} ₸
                              </p>
                            </div>

                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => removeItem(item)}
                              data-testid={`remove-item-${index}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* ── Summary ──────────────────────────────────────────────────── */}
        <div className="lg:col-span-1">
          <Card className="bg-white/90 backdrop-blur-sm sticky top-20" data-testid="cart-summary">
            <CardHeader>
              <CardTitle>Итого</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Товаров:</span>
                <span className="font-semibold">{cart.items.length}</span>
              </div>
              <div className="flex justify-between text-2xl font-bold border-t pt-4">
                <span>Сумма:</span>
                <span className="text-blue-600">{formatMoney(grandTotal)} ₸</span>
              </div>

              {/* Per-supplier breakdown */}
              {supplierGroups.length > 1 && (
                <div className="space-y-1.5 border-t pt-3">
                  {supplierGroups.map((group) => {
                    const supplierTotal = group.items.reduce(
                      (s, item) => s + (localQty[cartLineKey(item)] ?? item.quantity) * item.price,
                      0,
                    );
                    return (
                      <div key={group.supplier_id} className="flex justify-between text-xs text-gray-500">
                        <span className="truncate mr-2">{group.supplier_name}</span>
                        <span className="font-medium flex-shrink-0">{formatMoney(supplierTotal)} ₸</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* "Order All" — visible when more than one supplier */}
              {supplierGroups.length > 1 && (
                <Button
                  className="w-full"
                  size="lg"
                  onClick={createAllOrders}
                  disabled={anyBusy}
                  data-testid="checkout-all-button"
                >
                  {creatingAll
                    ? 'Оформляем все...'
                    : `Оформить все заказы (${supplierGroups.length})`}
                </Button>
              )}

              {/* Per-supplier order buttons */}
              {supplierGroups.map((group) => {
                const supplierTotal = group.items.reduce(
                  (s, item) => s + (localQty[cartLineKey(item)] ?? item.quantity) * item.price,
                  0,
                );
                const isBusy = creatingOrder === group.supplier_id;
                return (
                  <Button
                    key={group.supplier_id}
                    className="w-full"
                    size={supplierGroups.length > 1 ? 'sm' : 'lg'}
                    variant={supplierGroups.length > 1 ? 'outline' : 'default'}
                    onClick={() => createOrder(group.supplier_id)}
                    disabled={anyBusy}
                    data-testid={`checkout-button-${group.supplier_id}`}
                  >
                    {isBusy
                      ? 'Оформляем...'
                      : supplierGroups.length > 1
                        ? `${group.supplier_name} — ${formatMoney(supplierTotal)} ₸`
                        : 'Оформить заказ'}
                  </Button>
                );
              })}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
